/**
 * Doc mentions on screen (slice 6d spec §5.5; Paper A5e, A5f): a line's
 * mentions as chips, each with the doc's current title and opening it; the
 * @ picker under a line being edited, this Area's docs first, then the
 * others with their Area named, then New doc; and Docs it points to.
 */
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import { FileText, Plus } from "lucide-react";
import { Fragment } from "react";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { useCreateDoc, useDocs, type DocItem } from "../docs/api";
import type { BriefMention } from "./brief-api";
import { pickerRows, pieces, type Mention } from "./mentions";

const CHIP =
  "mx-0.5 inline-flex max-w-full items-center gap-1 rounded-[5px] bg-secondary px-1.5 align-baseline text-[0.93em] leading-[1.5] font-medium text-foreground";

/**
 * A mention's chip: its doc's title now (else as written). A link to the doc,
 * except inside a line that's itself a button (`plain`), where it's a label.
 * A reply's link to a doc shows as one too.
 */
export function DocChip({
  mention,
  title,
  plain,
}: {
  mention: Mention;
  title?: string;
  plain?: boolean;
}) {
  const label = (
    <>
      <FileText aria-hidden className="size-[0.9em] shrink-0 text-copy" />
      <span className="truncate">{title ?? mention.title}</span>
    </>
  );
  if (plain) {
    return (
      <span data-doc-mention={mention.id} className={CHIP}>
        {label}
      </span>
    );
  }
  return (
    <Link
      href={`/docs/${mention.id}`}
      data-doc-mention={mention.id}
      className={cn(CHIP, "hover:bg-secondary/70")}
    >
      {label}
    </Link>
  );
}

/** A brief's line with its mentions as chips. `titles`: each mentioned doc's title now. */
export function MentionText({
  text,
  titles,
  plain,
}: {
  text: string;
  titles?: ReadonlyMap<string, string>;
  /** Inside a button: the chips are labels, not links. */
  plain?: boolean;
}) {
  return (
    <>
      {pieces(text).map((p, n) => (
        <Fragment key={n}>
          {"text" in p ? (
            p.text
          ) : (
            <DocChip mention={p.mention} title={titles?.get(p.mention.id)} plain={plain} />
          )}
        </Fragment>
      ))}
    </>
  );
}

/** Each mentioned doc's title now, by id, from the brief's `mentions`. */
export const titlesOf = (mentions: readonly BriefMention[] | undefined) =>
  new Map((mentions ?? []).map((m) => [m.doc_id, m.title]));

function PickerRow({
  doc,
  aside,
  active,
  onPick,
}: {
  doc: DocItem;
  aside: string;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-picker-doc={doc.title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-foreground hover:bg-accent",
        active && "bg-accent",
      )}
    >
      <FileText aria-hidden className="size-4 shrink-0 text-copy" />
      <span className={cn("min-w-0 flex-1 truncate", active && "font-medium")}>{doc.title}</span>
      <span className="shrink-0 text-meta text-muted-foreground">{aside}</span>
    </button>
  );
}

const Heading = ({ children }: { children: string }) => (
  <div className="px-2.5 pt-2 pb-1 text-xs font-semibold tracking-label text-hint uppercase">
    {children}
  </div>
);

/** A group of the picker's docs under its heading; `active`: the one Enter takes. */
function PickerGroup({
  title,
  docs,
  aside,
  active,
  onPick,
}: {
  title: string;
  docs: DocItem[];
  aside: (d: DocItem) => string;
  active: DocItem | undefined;
  onPick: (d: DocItem) => void;
}) {
  if (docs.length === 0) return null;
  return (
    <>
      <Heading>{title}</Heading>
      {docs.map((d) => (
        <PickerRow
          key={d.id}
          doc={d}
          aside={aside(d)}
          active={d === active}
          onPick={() => onPick(d)}
        />
      ))}
    </>
  );
}

/** New doc called "…" (spec §5.5): an empty doc in this Area, mentioned at once. */
function NewDocRow({
  name,
  areaId,
  onPick,
}: {
  name: string;
  areaId: string;
  onPick: (m: Mention) => void;
}) {
  const create = useCreateDoc();
  return (
    <button
      type="button"
      data-picker-new
      disabled={create.isPending}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() =>
        create.mutate(
          { title: name.slice(0, 200), body: "", area_id: areaId },
          { onSuccess: (doc) => onPick({ id: doc.id, title: doc.title }) },
        )
      }
      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-copy hover:bg-accent"
    >
      <Plus aria-hidden className="size-4 shrink-0" />
      <span className="truncate">{`New doc called “${name}”`}</span>
    </button>
  );
}

/**
 * The @ picker (A5e): the docs matching `query`, this Area's first, and New
 * doc called "…". The first row is the one Enter takes (`first`).
 */
export function DocPicker({
  query,
  area,
  me,
  onPick,
  first,
}: {
  query: string;
  area: { id: string; slug: string; label: string };
  me: Me;
  onPick: (m: Mention) => void;
  /** Set to what Enter picks: the first doc, else nothing. */
  first: { current: (() => void) | null | undefined };
}) {
  const items = useFirstPage(query, area.slug);
  const { here, other, top } = pickerRows(items, area.id);
  const pick = (d: DocItem) => onPick({ id: d.id, title: d.title });
  first.current = top && (() => pick(top));
  const when = (d: DocItem) => shortTime(d.updated_at, me.workspace.timezone);
  return (
    <div
      role="listbox"
      aria-label="Point to a doc"
      data-doc-picker
      className="flex flex-col rounded-lg bg-popover p-1 shadow-lg ring-1 ring-foreground/10"
    >
      <PickerGroup
        title={`Docs in ${area.label}`}
        docs={here}
        aside={when}
        active={top}
        onPick={pick}
      />
      <PickerGroup
        title="Other docs"
        docs={other}
        aside={(d) => areaOrWhen(d, when)}
        active={top}
        onPick={pick}
      />
      <PickerFooter
        name={query.trim()}
        empty={items.length === 0}
        areaId={area.id}
        onPick={onPick}
      />
    </div>
  );
}

/** The picker's search: the first page of this Area's docs, then of everyone's. */
function useFirstPage(query: string, areaSlug: string): DocItem[] {
  return [...firstItems(useDocs("all", query, areaSlug)), ...firstItems(useDocs("all", query))];
}

const firstItems = (q: { data?: { pages: { items: DocItem[] }[] } }) =>
  q.data?.pages[0]?.items ?? [];

/** Another Area's doc names its Area, one with none when it changed. */
const areaOrWhen = (d: DocItem, when: (d: DocItem) => string) => d.area?.label ?? when(d);

/** New doc called "…" once something's typed, else a hint when there are no docs. */
function PickerFooter({
  name,
  empty,
  areaId,
  onPick,
}: {
  name: string;
  empty: boolean;
  areaId: string;
  onPick: (m: Mention) => void;
}) {
  if (name) return <NewDocRow name={name} areaId={areaId} onPick={onPick} />;
  if (!empty) return null;
  return <p className="px-2.5 py-2 text-meta text-muted-foreground">No docs yet. Type a name.</p>;
}

/** Docs it points to (spec §5.5): each mentioned doc, with when it was last edited. */
export function PointsTo({ mentions, me }: { mentions: readonly BriefMention[]; me: Me }) {
  if (mentions.length === 0) return null;
  return (
    <section
      data-brief-points-to
      className="flex flex-col gap-2.5 rounded-xl px-[18px] py-4 ring-1 ring-border ring-inset"
    >
      <h2 className="text-sm font-medium text-foreground">Docs it points to</h2>
      <ul className="flex flex-col gap-2">
        {mentions.map((m) => (
          <li key={m.doc_id} className="flex items-center gap-2">
            <FileText aria-hidden className="size-4 shrink-0 text-copy" />
            <Link
              href={`/docs/${m.doc_id}`}
              className={cn(
                "min-w-0 flex-1 truncate text-sm underline decoration-foreground/20 underline-offset-2",
                m.archived ? "text-hint" : "text-foreground",
              )}
            >
              {m.title}
            </Link>
            <span className="shrink-0 text-meta text-muted-foreground">
              {m.archived ? "Archived" : `Edited ${shortTime(m.updated_at, me.workspace.timezone)}`}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-meta text-muted-foreground">
        The crew opens a doc when the work needs it. Keep steps and plans in the doc; this page only
        points to it.
      </p>
    </section>
  );
}
